import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '@/config/api';

interface EmailValidationResult {
  isValid: boolean;
  isAvailable: boolean | null;
  isLoading: boolean;
  error: string | null;
}

export const useEmailValidation = (email: string, debounceMs: number = 500) => {
  const [result, setResult] = useState<EmailValidationResult>({
    isValid: false,
    isAvailable: null,
    isLoading: false,
    error: null
  });

  const checkEmailAvailability = useCallback(async (emailToCheck: string) => {
    if (!emailToCheck || emailToCheck.length < 3) {
      setResult({
        isValid: false,
        isAvailable: null,
        isLoading: false,
        error: null
      });
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToCheck)) {
      setResult({
        isValid: false,
        isAvailable: null,
        isLoading: false,
        error: 'Please enter a valid email address'
      });
      return;
    }

    setResult(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const apiUrl = API_BASE;

      const response = await fetch(`${apiUrl}/api/check-email?email=${encodeURIComponent(emailToCheck)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setResult({
          isValid: true,
          isAvailable: data.available,
          isLoading: false,
          error: data.available ? null : `A user with email "${emailToCheck}" already exists. Please use a different email address.`
        });
      } else if (response.status === 404) {
        setResult({
          isValid: true,
          isAvailable: true,
          isLoading: false,
          error: null
        });
      } else {
        setResult({
          isValid: true,
          isAvailable: null,
          isLoading: false,
          error: null // Don't show error for server issues, just skip validation
        });
      }
    } catch (_error) {
      // Don't show network errors to users, just skip validation
      setResult({
        isValid: true,
        isAvailable: null,
        isLoading: false,
        error: null
      });
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkEmailAvailability(email);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [email, debounceMs, checkEmailAvailability]);

  return result;
};
